import React, { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import Toast, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, getActiveAcademicYear, addActivityAsync, saveModuleData, loadSettings, getModuleCompletion } from "./utils";
import AdminAPI from "../../services/AdminAPI";

function Tag({ color, children }) {
  const colors = {
    green: "bg-[#eaf8f1] text-[#2d9b68] border border-[#b6e8d3]",
    red: "bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0]",
    orange: "bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]",
    blue: "bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]",
    gray: "bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function Window({ title, note, children, id }) {
  return (
    <section id={id} className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden mb-5">
      <div className="px-6 py-4 border-b border-[#e6ecf3]">
        <div className="font-semibold text-[#233445] text-[15px]">{title}</div>
        {note && <div className="text-xs text-[#617587] mt-0.5">{note}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function PCard({ label, value }) {
  return (
        <div className="bg-[#f5f7fb] rounded-[14px] p-4 mb-3 last:mb-0">
      <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-[#233445]">{value || "Not available yet."}</div>
    </div>
  );
}

const MODULE_KEYS = ["identity","schoolInfo","teams","market","diagnosis","swot","goals","actions","monitoring"];

function getOverall() {
  const vals = MODULE_KEYS.map(getModuleCompletion);
  return Math.round(vals.reduce((a,b)=>a+b,0)/vals.length);
}

function statusColor(p) {
  if(p>=80) return "green"; if(p>=50) return "orange"; if(p>0) return "blue"; return "red";
}

function sevColor(s) { return s==="Critical"?"red":s==="High"?"orange":s==="Medium"?"blue":"gray"; }
function urgColor(u) { return u==="Immediate"?"red":u==="Short-Term"?"orange":"blue"; }

function mapLevelToSeverity(level) {
  if (level <= 1) return "Critical";
  if (level <= 2) return "High";
  return "Medium";
}

function mapLevelToUrgency(level) {
  if (level <= 1) return "Immediate";
  if (level <= 2) return "Short-Term";
  return "Long-Term";
}

function buildSQAAFDomainAnalysis(audit, framework) {
  const domains = framework?.domains || framework || [];
  const indicatorLookup = new Map();

  domains.forEach(domain => {
    (domain.subdomains || []).forEach(subdomain => {
      (subdomain.indicators || []).forEach(indicator => {
        indicatorLookup.set(indicator.indicator_id, {
          category: domain.domain_name || domain.domain_id || "SQAAF Domain",
          area: subdomain.subdomain_name || subdomain.subdomain_id || "",
          title: indicator.statement || indicator.indicator_id || "",
        });
      });
    });
  });

  const strengths = [];
  const improvements = [];

  (audit?.responses || [])
    .filter(resp => Number(resp?.selected_level) > 0)
    .forEach(resp => {
      const level = Number(resp?.selected_level) || 0;
      const meta = indicatorLookup.get(resp.indicator_id) || {};
      const item = {
        category: meta.category || "SQAAF Domain",
        area: meta.area || "",
        title: meta.title || resp.indicator_id || "SQAAF indicator",
        description: `Indicator ${resp.indicator_id || ""} is currently at Level ${level}.`,
        level,
        severity: mapLevelToSeverity(level),
        urgency: mapLevelToUrgency(level),
      };
      if (level >= 3) strengths.push(item);
      else improvements.push(item);
    });

  return { strengths, improvements };
}

function groupByDomain(items = []) {
  return items.reduce((acc, item) => {
    const domain = item?.category || "SQAAF Domain";
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(item);
    return acc;
  }, {});
}

function buildHighlights() {
  const highlights = [];
  const identity = readStorage(STORAGE_KEYS.identity, {});
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const goals = readStorage(STORAGE_KEYS.goals, { goals: [] });
  const monitoring = readStorage(STORAGE_KEYS.monitoring, { reviews: [] });
  if(identity.vision && identity.mission) highlights.push("The school has a defined vision and mission, which gives direction to planning.");
  const strengths = (swot.points||[]).filter(p=>p.quadrant==="Strength");
  if(strengths.length) highlights.push(`The school has identified ${strengths.length} strategic strength(s).`);
  if((goals.goals||[]).length) highlights.push(`The school has ${goals.goals.length} strategic goal(s) defined.`);
  if((monitoring.reviews||[]).length) highlights.push(`Monitoring has started with ${monitoring.reviews.length} checkpoint log(s).`);
  return highlights.length ? highlights : ["More modules need meaningful input before highlights can be generated."];
}

function buildConcerns() {
  const concerns = [];
  const diagnosis = readStorage(STORAGE_KEYS.diagnosis, { findings: [] });
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const market = readStorage(STORAGE_KEYS.market, { signals: [] });
  const monitoring = readStorage(STORAGE_KEYS.monitoring, { reviews: [] });
  const highFindings = (diagnosis.findings||[]).filter(f=>f.severity==="High"||f.severity==="Critical");
  if(highFindings.length) concerns.push(`${highFindings.length} high-severity diagnostic issue(s) need attention.`);
  const threats = (swot.points||[]).filter(p=>p.quadrant==="Threat"&&p.priority==="High");
  if(threats.length) concerns.push(`${threats.length} high-priority threat(s) are visible in SWOT.`);
  const highThreats = (market.signals||[]).filter(s=>s.type==="Threat"&&s.impact==="High");
  if(highThreats.length) concerns.push(`${highThreats.length} high-impact external threat signal(s) are present.`);
  const blockers = (monitoring.reviews||[]).filter(r=>r.blocker&&r.blocker.trim()!=="");
  if(blockers.length) concerns.push(`${blockers.length} monitoring log(s) report blockers or execution risks.`);
  return concerns.length ? concerns : ["No major concern clusters visible yet."];
}

function getNextStep(overall, goals) {
  if(overall>=80) return "Use the complete view for review meetings and begin tightening weak modules through regular monitoring cycles.";
  if(overall>=50) return "Strengthen lower-completion modules and sharpen KPI, ownership, and monitoring depth.";
  if(!(goals||[]).length) return "Translate diagnosis and SWOT into clearer goals before moving too quickly into reporting.";
  return "Complete more modules before relying on this reporting view.";
}

function getCommentary(overall) {
  if(overall>=80) return "The school has built a fairly mature planning structure across identity, diagnosis, strategy, execution, and review.";
  if(overall>=50) return "The school has a useful planning base, but some modules still need stronger detail before full strategic confidence is possible.";
  if(overall>0) return "The school has started building a planning structure, but it is still too incomplete for strong reporting.";
  return "The school does not yet have enough data for a meaningful summary.";
}

const TABS = [
  { id: "heroSection", label: "Overview" },
  { id: "identitySection", label: "Identity" },
  { id: "profileSection", label: "School Profile" },
  { id: "governanceSection", label: "Governance" },
  { id: "marketSection", label: "Market" },
  { id: "diagnosisSection", label: "Diagnosis" },
  { id: "swotSection", label: "SWOT" },
  { id: "goalsSection", label: "Goals" },
  { id: "actionsSection", label: "Action Plans" },
  { id: "monitoringSection", label: "Monitoring" },
  { id: "reportSection", label: "Final Summary" },
];

export default function CompleteSchoolView() {
  const [viewMode, setViewMode] = useState("executive");
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("heroSection");
  const [refreshKey, setRefreshKey] = useState(0);
  const [school, setSchool] = useState(getSchoolSettings);
  const [sqaafDiagnosisFindings, setSqaafDiagnosisFindings] = useState([]);
  const [sqaafStrengths, setSqaafStrengths] = useState([]);

  useEffect(() => {
    loadSettings().then(s => { if (s?.schoolName) setSchool(s); });
    AdminAPI.sdpGetBulk(getActiveAcademicYear()).then(res => {
      if (res?.modules) {
        Object.entries(res.modules).forEach(([key, data]) => {
          if (!data || !STORAGE_KEYS[key]) return;
          // Normalise goals: ensure title is always populated
          if (key === "goals" && data.goals) {
            data = {
              ...data,
              goals: data.goals.map(g => ({ ...g, title: g.title || g.goal || "", goal: g.goal || g.title || "" })),
            };
          }
          saveStorage(STORAGE_KEYS[key], data);
        });
        setRefreshKey(k => k + 1);
      }
    }).catch(() => {});

    const loadSQAAFDiagnosis = async () => {
      try {
        const year = getActiveAcademicYear();
        const [audit, framework] = await Promise.all([
          AdminAPI.sqaafGetCurrentAudit(year),
          AdminAPI.sqaafGetFrameworkOverview(),
        ]);
        const analysis = buildSQAAFDomainAnalysis(audit, framework);
        setSqaafDiagnosisFindings(analysis.improvements || []);
        setSqaafStrengths(analysis.strengths || []);

        // Cache SQAAF completion so getModuleCompletion("diagnosis") can use it
        const domains = framework?.domains || framework || [];
        let totalIndicators = 0;
        domains.forEach(d => {
          (d.subdomains || []).forEach(sd => {
            totalIndicators += (sd.indicators || []).length;
          });
        });
        const answeredResponses = (audit?.responses || []).filter(r => Number(r?.selected_level) > 0);
        const sqaafPct = totalIndicators > 0
          ? Math.round((answeredResponses.length / totalIndicators) * 100)
          : 0;
        saveStorage(STORAGE_KEYS.sqaafCompletionCache, { percent: sqaafPct, year });
        setRefreshKey(k => k + 1);
      } catch (_) {
        setSqaafDiagnosisFindings([]);
        setSqaafStrengths([]);
      }
    };

    loadSQAAFDiagnosis();
  }, []);

  const identity = readStorage(STORAGE_KEYS.identity, {});
  const schoolInfo = readStorage(STORAGE_KEYS.schoolInfo, {});
  const teams = readStorage(STORAGE_KEYS.teams, { leadership: [], committees: [] });
  const market = readStorage(STORAGE_KEYS.market, { competitors: [], signals: [] });
  const diagnosis = readStorage(STORAGE_KEYS.diagnosis, { findings: [] });
  const diagnosisFindings = sqaafDiagnosisFindings.length
    ? sqaafDiagnosisFindings
    : (diagnosis.findings || []);
  const hasSQAAFDomainAnalysis = sqaafStrengths.length > 0 || sqaafDiagnosisFindings.length > 0;
  const strengthsByDomain = groupByDomain(sqaafStrengths);
  const improvementsByDomain = groupByDomain(sqaafDiagnosisFindings);
  const analysisDomains = Array.from(
    new Set([...Object.keys(strengthsByDomain), ...Object.keys(improvementsByDomain)])
  );
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const goalsData = readStorage(STORAGE_KEYS.goals, { goals: [] });
  const actionsData = readStorage(STORAGE_KEYS.actions, { actions: [] });
  const monitoringData = readStorage(STORAGE_KEYS.monitoring, { reviews: [] });

  const overall = getOverall();
  const readiness = overall>=80 ? "High" : overall>=50 ? "Medium" : "Low";
  const readinessColor = readiness==="High" ? "green" : readiness==="Medium" ? "orange" : "red";
  const goalsCount = (goalsData.goals||[]).length + (actionsData.actions||[]).length;
  const monitoringCount = (monitoringData.reviews||[]).length;

  const highlights = buildHighlights();
  const concerns = buildConcerns();
  const commentary = getCommentary(overall);
  const nextStep = getNextStep(overall, goalsData.goals);

  const heroText = overall >= 80
    ? `${school.schoolName} has built a comprehensive planning framework. Identity, strategy, execution, and monitoring are all meaningfully populated.`
    : overall >= 50
    ? `${school.schoolName} has a developing planning structure. Most major areas have been started, but execution and monitoring still need strengthening.`
    : `${school.schoolName} is in the early stages of building its development planning framework. Several key modules still need meaningful input.`;

  const heroReadout = overall >= 80
    ? "Strong planning maturity. Ready for formal review cycles."
    : overall >= 50
    ? "Developing maturity. Key modules active but not yet complete."
    : "Early stage. Most modules still need significant input.";

  function handleRefresh() {
    setRefreshKey(k => k+1);
    showToast("Complete view refreshed.", "success");
  }

  function handleSaveSnapshot() {
    const snapshot = {
      savedAt: new Date().toISOString(),
      overall, readiness,
      modules: MODULE_KEYS.map(k => ({ key: k, completion: getModuleCompletion(k) })),
    };
    saveModuleData("compiled", snapshot, overall);
    addActivityAsync("Complete School View", "Snapshot saved", "Saved", "green");
    showToast("Snapshot saved successfully.", "success");
  }

  function handlePrint() {
    const readinessHex = readiness === "High" ? "#2d9b68" : readiness === "Medium" ? "#f47c20" : "#d95353";
    const moduleLabels = { identity:"Institutional Identity", schoolInfo:"School Profile", teams:"Teams & Governance", market:"Market & Context", diagnosis:"Diagnosis", swot:"SWOT", goals:"Strategic Goals", actions:"Action Plans", monitoring:"Monitoring" };

    const row = (cells, bold = false) => `<tr>${cells.map(c => `<td style="padding:7px 12px;border-bottom:1px solid #e6ecf3;font-size:12px;color:${bold?"#233445":"#617587"};font-weight:${bold?"600":"400"}">${c||"—"}</td>`).join("")}</tr>`;
    const thead = cells => `<tr>${cells.map(c => `<th style="padding:8px 12px;background:#f5f7fb;border-bottom:2px solid #e6ecf3;font-size:11px;color:#617587;font-weight:600;text-align:left;text-transform:uppercase;letter-spacing:.04em">${c}</th>`).join("")}</tr>`;
    const table = (headers, rows) => `<table style="width:100%;border-collapse:collapse;border:1px solid #e6ecf3;border-radius:10px;overflow:hidden;margin-bottom:16px"><thead>${thead(headers)}</thead><tbody>${rows}</tbody></table>`;
    const section = (title, note, content) => `<div style="margin-bottom:28px;page-break-inside:avoid"><div style="border-bottom:2px solid #f47c20;padding-bottom:6px;margin-bottom:12px"><div style="font-size:15px;font-weight:700;color:#233445">${title}</div>${note?`<div style="font-size:11px;color:#8ea0b1;margin-top:2px">${note}</div>`:""}</div>${content}</div>`;
    const card = (label, value) => value ? `<div style="background:#f5f7fb;border-radius:10px;padding:12px 14px;margin-bottom:8px"><div style="font-size:10px;font-weight:700;color:#8ea0b1;text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px">${label}</div><div style="font-size:13px;color:#233445">${value}</div></div>` : "";
    const badge = (text, color) => `<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600;background:${color}22;color:${color};border:1px solid ${color}44">${text}</span>`;

    const swotGrid = ["Strength","Weakness","Opportunity","Threat"].map(q => {
      const pts = (swot.points||[]).filter(p=>p.quadrant===q);
      const colors = {Strength:"#2d9b68",Weakness:"#d95353",Opportunity:"#2d81c1",Threat:"#d58d1f"};
      const c = colors[q];
      return `<div style="border:1px solid ${c}33;border-radius:10px;overflow:hidden"><div style="background:${c}11;padding:8px 12px;font-size:12px;font-weight:700;color:${c}">${q}s</div><div style="padding:10px 12px">${pts.length?pts.map(p=>`<div style="margin-bottom:6px;font-size:12px;color:#233445;font-weight:600">${p.title}<br><span style="font-size:11px;color:#617587;font-weight:400">${p.description||""}</span></div>`).join(""):`<div style="font-size:11px;color:#8ea0b1">No entries yet.</div>`}</div></div>`;
    }).join("");

    const diagnosisDomainPrint = hasSQAAFDomainAnalysis
      ? analysisDomains.map((domainName) => {
          const domainStrengths = strengthsByDomain[domainName] || [];
          const domainImprovements = improvementsByDomain[domainName] || [];
          const strengthsRows = domainStrengths.length
            ? domainStrengths.map(item => row([item.title, `L${item.level}`])).join("")
            : row(["No strengths in this domain.", "—"]);
          const improvementsRows = domainImprovements.length
            ? domainImprovements.map(item => row([item.title, `L${item.level}`])).join("")
            : row(["No improvement areas in this domain.", "—"]);

          return `
            <div style="margin-bottom:18px;border:1px solid #e6ecf3;border-radius:10px;overflow:hidden;page-break-inside:avoid">
              <div style="padding:10px 12px;background:#f5f7fb;border-bottom:1px solid #e6ecf3;font-size:13px;font-weight:700;color:#233445">
                ${domainName}
              </div>
              <div style="padding:10px 12px">
                <div style="font-size:12px;font-weight:700;color:#2d9b68;margin-bottom:6px">Strengths (${domainStrengths.length})</div>
                ${table(["Indicator", "Level"], strengthsRows)}
                <div style="font-size:12px;font-weight:700;color:#b8611c;margin:8px 0 6px">Areas for Improvement (${domainImprovements.length})</div>
                ${table(["Indicator", "Level"], improvementsRows)}
              </div>
            </div>
          `;
        }).join("")
      : (diagnosisFindings.length
          ? table(
              ["Category","Finding","Severity","Urgency"],
              diagnosisFindings.map(f=>row([f.category,f.title,f.severity,f.urgency])).join("")
            )
          : "No diagnosis findings yet.");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${school.schoolName} — School Development Report</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#233445;background:#fff;padding:0}
  @page{size:A4;margin:18mm 16mm}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  .cover{min-height:240px;background:linear-gradient(135deg,#1D3469 0%,#2d81c1 100%);color:#fff;padding:40px 48px 36px;margin-bottom:28px;border-radius:0 0 18px 18px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .grid4{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
  .swot-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}
  .stat-box{background:#f5f7fb;border-radius:10px;padding:14px 16px;border:1px solid #e6ecf3}
  .page-break{page-break-before:always}
  .footer{margin-top:32px;padding-top:12px;border-top:1px solid #e6ecf3;font-size:10px;color:#8ea0b1;text-align:center}
</style>
</head><body>

<div class="cover">
  <div style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;opacity:.7;margin-bottom:10px">School Development Report • Academic Year ${school.academicYear}</div>
  <div style="font-size:28px;font-weight:700;margin-bottom:6px">${school.schoolName}</div>
  <div style="font-size:14px;opacity:.8;margin-bottom:20px">Complete School Development Plan — All Modules</div>
  <div style="display:flex;gap:12px;flex-wrap:wrap">
    ${badge("Readiness: "+readiness,"#fff")}
    ${badge(overall+"% Complete","#fff")}
    ${badge("Generated: "+new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}),"#fff")}
  </div>
</div>

<div style="padding:0 4px">

${section("Module Completion Overview","Status across all 9 planning modules.",
  `<div class="grid4">${MODULE_KEYS.map(k=>{const p=getModuleCompletion(k);const c=p>=80?"#2d9b68":p>=50?"#f47c20":p>0?"#2d81c1":"#d95353";return`<div class="stat-box"><div style="font-size:10px;color:#8ea0b1;margin-bottom:4px">${moduleLabels[k]||k}</div><div style="font-size:20px;font-weight:700;color:${c}">${p}%</div><div style="height:4px;background:#e6ecf3;border-radius:4px;margin-top:6px"><div style="height:4px;background:${c};border-radius:4px;width:${p}%"></div></div></div>`;}).join("")}</div>`
)}

${section("Institutional Identity","Vision, mission, values, culture, and outcome.",
  card("Vision",identity.vision)+card("Mission",identity.mission)+
  card("Values",(identity.values||[]).map(v=>v.name||v).filter(Boolean).join(", "))+
  card("Culture Statement",identity.cultureStatement)+card("Ultimate Outcome",identity.ultimateOutcome)
)}

${section("School Profile","Core institutional baseline.",
  `<div class="grid2">${card("Institution",([schoolInfo.schoolName,schoolInfo.board,schoolInfo.schoolType].filter(Boolean).join(" | ")))}</div>`+
  `<div class="grid2">${card("Headcount",[schoolInfo.studentStrength?`Students: ${schoolInfo.studentStrength}`:"",schoolInfo.staffStrength?`Staff: ${schoolInfo.staffStrength}`:""].filter(Boolean).join(" | "))
  }${card("Location / Contact",[schoolInfo.city,schoolInfo.contactEmail].filter(Boolean).join(" | "))}</div>`+
  ((schoolInfo.infrastructure||[]).length?table(["Infrastructure Area","Status","Remark"],(schoolInfo.infrastructure||[]).map(i=>row([i.name,i.status,i.remark])).join("")):"")
)}

${section("Leadership, Teams & Governance","Ownership structure and committees.",
  `<div class="grid2">${card("Main Accountability Owner",teams.mainOwner)}${card("Review Frequency",teams.reviewFrequency)}</div>`+
  ((teams.leadership||[]).length?table(["Role","Name","Primary Area"],(teams.leadership||[]).map(l=>row([l.role,l.name,l.area])).join("")):"No leadership data.")+
  ((teams.committees||[]).length?table(["Committee","Lead","Meeting Frequency"],(teams.committees||[]).map(c=>row([c.name,c.lead,c.frequency])).join("")):"")
)}

${section("Market & External Context","Competitive landscape and external signals.",
  `<div class="grid2">${card("Parent Expectations",market.parentExpectations)}${card("Community Needs",market.communityNeeds)}</div>`+
  ((market.competitors||[]).length?table(["Competitor","Type","Pressure Level"],(market.competitors||[]).map(c=>row([c.name,c.type,c.pressure])).join("")):"No competitor data.")+
  ((market.signals||[]).length?table(["Signal Type","Title","Impact"],(market.signals||[]).map(s=>row([s.type,s.title,s.impact])).join("")):"")
)}

<div class="page-break"></div>

${section("Diagnosis","Internal findings, gaps, and critical issues.",
  diagnosisDomainPrint
)}

${section("SWOT Analysis","Strengths, weaknesses, opportunities, and threats.",
  `<div class="swot-grid">${swotGrid}</div>`
)}

${section("Strategic Goals","Goals with owners, timelines, and KPIs.",
  ((goalsData.goals||[]).length?table(["Category","Goal","Owner","Timeline","KPI"],(goalsData.goals||[]).map(g=>row([g.category,g.title||g.goal,g.owner,g.timeline||g.target_date,g.kpi])).join("")):"No goals defined yet.")
)}

${section("Action Plans","Task-level execution mapped to goals.",
  ((actionsData.actions||[]).length?table(["Linked Goal","Action Item","Owner","Status","Start → End"],(actionsData.actions||[]).map(a=>row([a.goal,a.title,a.owner,a.status,[a.startDate,a.endDate].filter(Boolean).join(" → ")])).join("")):"No action plans yet.")
)}

${section("Monitoring & Review Logs","Progress checkpoints, blockers, and next review dates.",
  ((monitoringData.reviews||[]).length?table(["Linked Goal","Action","Status","Review Date","Blocker/Risk"],(monitoringData.reviews||[]).map(r=>row([r.goal,r.action,r.status,r.reviewDate,r.blocker||"None"])).join("")):"No monitoring logs yet.")
)}

<div class="page-break"></div>

${section("Final Summary & Readiness Assessment","",
  `<div class="grid2">${card("Overall Completion",overall+"% across all 9 modules")}${card("Planning Readiness",readiness+" — "+heroReadout)}</div>`+
  card("Strategic Commentary",commentary)+card("Recommended Next Step",nextStep)+
  `<div style="margin-top:16px">${highlights.map(h=>`<div style="background:#eaf8f1;border:1px solid #b6e8d3;border-radius:10px;padding:10px 14px;margin-bottom:8px;font-size:12px;color:#617587"><span style="font-weight:700;color:#2d9b68">Highlight: </span>${h}</div>`).join("")}</div>`+
  `<div style="margin-top:8px">${concerns.filter(c=>!c.startsWith("No major")).map(c=>`<div style="background:#fff0f0;border:1px solid #f5c0c0;border-radius:10px;padding:10px 14px;margin-bottom:8px;font-size:12px;color:#617587"><span style="font-weight:700;color:#d95353">Concern: </span>${c}</div>`).join("")}</div>`
)}

</div>
<div class="footer">Holistic Evaluator AI • ${school.schoolName} • School Development Portal • ${school.academicYear} • Printed ${new Date().toLocaleString()}</div>
</body></html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  }

  function scrollTo(sectionId) {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const leaders = (teams.leadership || []).filter(l =>
    !search || `${l.role} ${l.name} ${l.area}`.toLowerCase().includes(search.toLowerCase())
  );
  const committees = (teams.committees || []).filter(c =>
    !search || `${c.name} ${c.lead} ${c.purpose}`.toLowerCase().includes(search.toLowerCase())
  );
  const competitors = (market.competitors || []).filter(c =>
    !search || `${c.name} ${c.type} ${c.pressure}`.toLowerCase().includes(search.toLowerCase())
  );
  const signals = (market.signals || []).filter(s =>
    !search || `${s.type} ${s.title} ${s.impact}`.toLowerCase().includes(search.toLowerCase())
  );
  const findings = diagnosisFindings.filter(f =>
    !search || `${f.category} ${f.title} ${f.severity}`.toLowerCase().includes(search.toLowerCase())
  );
  const goals = (goalsData.goals || []).filter(g =>
    !search || `${g.category} ${g.title} ${g.owner}`.toLowerCase().includes(search.toLowerCase())
  );
  const actions = (actionsData.actions || []).filter(a =>
    !search || `${a.goal} ${a.title} ${a.owner}`.toLowerCase().includes(search.toLowerCase())
  );
  const reviews = (monitoringData.reviews || []).filter(r =>
    !search || `${r.goal || ""} ${r.action || ""} ${r.status || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const swotQuadrants = [
    { key: "Strength", label: "Strengths", bg: "bg-[#eaf8f1]", border: "border-[#b6e8d3]", textColor: "text-[#2d9b68]" },
    { key: "Weakness", label: "Weaknesses", bg: "bg-[#fff0f0]", border: "border-[#f5c0c0]", textColor: "text-[#d95353]" },
    { key: "Opportunity", label: "Opportunities", bg: "bg-[#eef6fc]", border: "border-[#cfe3f3]", textColor: "text-[#2d81c1]" },
    { key: "Threat", label: "Threats", bg: "bg-[#fff7e7]", border: "border-[#f5d89a]", textColor: "text-[#d58d1f]" },
  ];

  const TH = ({ children }) => <th className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{children}</th>;
  const TD = ({ children }) => <td className="px-4 py-3 text-sm text-[#617587]">{children}</td>;
  const TDstrong = ({ children }) => <td className="px-4 py-3 text-sm font-medium text-[#233445]">{children}</td>;
  const EmptyRow = ({ cols, msg }) => <tr><td colSpan={cols} className="text-center py-8 text-[#8ea0b1] text-sm">{msg}</td></tr>;

  return (
    <>
      <div key={refreshKey} className="flex-1 flex flex-col min-w-0 border-l-2 border-slate-200 ml-1">
        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
          <section className="mb-8 print:hidden">
            <div className="text-center px-4 py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Complete School View</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                Review the full connected planning picture across identity, strategy, execution, monitoring, and final readiness.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 print:hidden">
            {[
              { label: "Overall Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${overall}%`, meta: "combined across all planning modules" },
              { label: "Goals & Actions", icon: "🎯", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: goalsCount, meta: "goals + execution items" },
              { label: "Monitoring Logs", icon: "📝", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: monitoringCount, meta: "review checkpoints recorded" },
              { label: "School Readiness", icon: "🧭", iconCls: readinessColor === "green" ? "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]" : readinessColor === "orange" ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: readiness, meta: "planning and execution maturity" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-[10px] mb-4">
                  <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                  <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                    {s.icon}
                  </div>
                </div>
                <div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Progress */}
          <div className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm p-5 mb-6 print:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#233445]">Overall Planning Completion</span>
              <span className="text-sm font-bold text-[#f47c20]">{overall}%</span>
            </div>
            <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
              <div className="h-full bg-[#f47c20] rounded-full transition-all duration-500" style={{ width: `${overall}%` }} />
            </div>
          </div>

          {/* Section tabs */}
          <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-[18px] border border-[#e6ecf3] p-1.5 shadow-sm print:hidden">
            {TABS.map(t => (
              <button key={t.id} onClick={() => scrollTo(t.id)}
                className={`px-3 py-1.5 rounded-[10px] text-xs font-medium transition-colors ${activeSection===t.id ? "bg-[#f47c20] text-white" : "text-[#617587] hover:text-[#233445] hover:bg-[#f5f7fb]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* HERO SECTION */}
          <Window id="heroSection" title={`${school.schoolName} — Complete Development View`} note="Overall planning maturity based on all saved modules.">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
              <div>
                <p className="text-sm text-[#617587] mb-4">{heroText}</p>
                <div className="grid grid-cols-3 gap-3">
                  {MODULE_KEYS.slice(0,9).map(k => {
                    const pct = getModuleCompletion(k);
                    const label = { identity:"Identity", schoolInfo:"School Info", teams:"Teams", market:"Market", diagnosis:"Diagnosis", swot:"SWOT", goals:"Goals", actions:"Actions", monitoring:"Monitoring" }[k];
                    return (
                      <div key={k} className="bg-[#f5f7fb] rounded-[12px] p-3 border border-[#e6ecf3]">
                        <div className="text-xs text-[#617587] mb-1">{label}</div>
                        <div className={`text-lg font-bold ${statusColor(pct)==="green"?"text-[#2d9b68]":statusColor(pct)==="red"?"text-[#d95353]":statusColor(pct)==="orange"?"text-[#f47c20]":"text-[#2d81c1]"}`}>{pct}%</div>
                        <div className="h-1 bg-[#e6ecf3] rounded-full mt-1"><div className={`h-full rounded-full ${statusColor(pct)==="green"?"bg-[#2d9b68]":statusColor(pct)==="red"?"bg-[#d95353]":statusColor(pct)==="orange"?"bg-[#f47c20]":"bg-[#2d81c1]"}`} style={{ width:`${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-[#f47c20]/5 border border-[#f7c9a1] rounded-[18px] p-5">
                <div className="text-sm font-semibold text-[#f47c20] mb-2">Immediate Readout</div>
                <p className="text-sm text-[#617587]">{heroReadout}</p>
                <div className="mt-4 flex items-center gap-2">
                  <Tag color={readinessColor}>Readiness: {readiness}</Tag>
                  <Tag color="blue">{overall}% Complete</Tag>
                </div>
              </div>
            </div>
          </Window>

          {/* IDENTITY */}
          <Window id="identitySection" title="Institutional Identity" note="Vision, mission, values, culture, and ultimate outcome.">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div>
                <PCard label="Vision" value={identity.vision} />
                <PCard label="Mission" value={identity.mission} />
              </div>
              <div>
                <PCard label="Values" value={identity.values?.length ? identity.values.map(v => v.name || v).join(", ") : null} />
                <PCard
                  label="Culture & Outcome"
                  value={[identity.cultureStatement, identity.ultimateOutcome].filter(Boolean).join(" | ") || null}
                />
              </div>
            </div>
          </Window>

          {/* SCHOOL PROFILE */}
          <Window id="profileSection" title="School Profile & Baseline" note="Core profile, academic baseline, infrastructure, and contact snapshot.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <PCard label="Institution Profile" value={[schoolInfo.schoolName, schoolInfo.board, schoolInfo.schoolType].filter(Boolean).join(" | ") || null} />
              <PCard
                label="Academic Baseline"
                value={[
                  schoolInfo.avgClassSize ? `Avg Class: ${schoolInfo.avgClassSize}` : "",
                  schoolInfo.studentTeacherRatio ? `Ratio: ${schoolInfo.studentTeacherRatio}` : "",
                  schoolInfo.academicOfferings?.length ? `Offerings: ${schoolInfo.academicOfferings.length}` : "",
                ].filter(Boolean).join(" | ") || null}
              />
              <PCard
                label="Operations & Contact"
                value={[
                  schoolInfo.studentStrength ? `Students: ${schoolInfo.studentStrength}` : "",
                  schoolInfo.staffStrength ? `Staff: ${schoolInfo.staffStrength}` : "",
                  schoolInfo.city ? schoolInfo.city : "",
                ].filter(Boolean).join(" | ") || null}
              />
            </div>
            {viewMode === "detailed" && (
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Infrastructure Area</TH><TH>Status</TH><TH>Remark</TH></tr></thead>
                  <tbody>
                    {!(schoolInfo.infrastructure||[]).length ? <EmptyRow cols={3} msg="No infrastructure data available yet." /> :
                      (schoolInfo.infrastructure||[]).map((it, i) => (
                        <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                          <TDstrong>{it.name}</TDstrong>
                          <TD><Tag color={it.status==="Available"?"green":it.status==="Partially Available"?"blue":it.status==="Needs Upgrade"?"orange":"red"}>{it.status}</Tag></TD>
                          <TD>{it.remark||"—"}</TD>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </Window>

          {/* GOVERNANCE */}
          <Window id="governanceSection" title="Leadership, Teams & Governance" note="Leadership team, committees, ownership, and review rhythm.">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div>
                <PCard label="Leadership Summary" value={(teams.leadership||[]).length ? `${teams.leadership.length} leadership role(s) defined.` : null} />
                <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Role</TH><TH>Name</TH><TH>Primary Area</TH></tr></thead>
                    <tbody>
                      {!leaders.length ? <EmptyRow cols={3} msg="No leadership data available yet." /> :
                        leaders.map((l,i) => (
                          <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                            <TDstrong>{l.role}</TDstrong><TD>{l.name}</TD><TD>{l.area}</TD>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <PCard label="Governance & Committees" value={(teams.committees||[]).length ? `${teams.committees.length} committee(s) defined. Owner: ${teams.mainOwner||"Not set"}. Review: ${teams.reviewFrequency||"Not set"}.` : null} />
                <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Committee</TH><TH>Lead</TH><TH>Frequency</TH></tr></thead>
                    <tbody>
                      {!committees.length ? <EmptyRow cols={3} msg="No committee data available yet." /> :
                        committees.map((c,i) => (
                          <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                            <TDstrong>{c.name}</TDstrong><TD>{c.lead}</TD><TD>{c.frequency}</TD>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Window>

          {/* MARKET */}
          <Window id="marketSection" title="Market & External Context" note="Parent expectations, community needs, competitor view, and external signals.">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
              <PCard label="Parent & Community Context" value={[market.parentExpectations ? `Parents: ${market.parentExpectations}` : "", market.communityNeeds ? `Community: ${market.communityNeeds}` : ""].filter(Boolean).join(" | ") || null} />
              <PCard label="Admissions & Future Demand" value={[market.admissionsTrend ? `Trend: ${market.admissionsTrend}` : "", market.futureDemand ? `Future: ${market.futureDemand}` : ""].filter(Boolean).join(" | ") || null} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Competitor</TH><TH>Type</TH><TH>Pressure</TH></tr></thead>
                  <tbody>
                    {!competitors.length ? <EmptyRow cols={3} msg="No competitor data available yet." /> :
                      competitors.map((c,i) => (
                        <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                          <TDstrong>{c.name}</TDstrong><TD>{c.type}</TD>
                          <TD><Tag color={c.pressure==="High"?"red":c.pressure==="Medium"?"orange":"blue"}>{c.pressure}</Tag></TD>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Signal Type</TH><TH>Title</TH><TH>Impact</TH></tr></thead>
                  <tbody>
                    {!signals.length ? <EmptyRow cols={3} msg="No external signal data available yet." /> :
                      signals.map((s,i) => (
                        <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                          <TD><Tag color={s.type==="Opportunity"?"green":"red"}>{s.type}</Tag></TD>
                          <TDstrong>{s.title}</TDstrong>
                          <TD><Tag color={s.impact==="High"?"red":s.impact==="Medium"?"orange":"blue"}>{s.impact}</Tag></TD>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </Window>

          {/* DIAGNOSIS */}
          <Window id="diagnosisSection" title="Diagnosis" note="Current school condition, issue identification, and critical diagnostic findings.">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
              <PCard
                label="Overall Diagnostic View"
                value={hasSQAAFDomainAnalysis
                  ? `${sqaafStrengths.length} strength indicator(s) and ${sqaafDiagnosisFindings.length} improvement indicator(s) from current SQAAF detailed analysis.`
                  : (diagnosisFindings.length ? `${diagnosisFindings.length} finding(s) logged across ${[...new Set(diagnosisFindings.map(f=>f.category))].length} category(ies).` : null)}
              />
              <PCard
                label="Priority Area"
                value={hasSQAAFDomainAnalysis
                  ? (sqaafDiagnosisFindings.find(f => f.level <= 1)?.category || sqaafDiagnosisFindings[0]?.category || null)
                  : (diagnosisFindings.find(f=>f.severity==="Critical")?.category || diagnosisFindings.find(f=>f.severity==="High")?.category || null)}
              />
            </div>
            {hasSQAAFDomainAnalysis ? (
              <div className="space-y-4">
                {analysisDomains.map((domainName, domainIdx) => {
                  const domainStrengths = strengthsByDomain[domainName] || [];
                  const domainImprovements = improvementsByDomain[domainName] || [];
                  return (
                    <div key={`domain-analysis-${domainIdx}`} className="rounded-[14px] border border-[#e6ecf3] bg-white p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-base font-semibold text-[#233445]">{domainName}</div>
                        <div className="flex gap-2">
                          <Tag color="green">Strengths: {domainStrengths.length}</Tag>
                          <Tag color="orange">Improvements: {domainImprovements.length}</Tag>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-[12px] border border-[#b6e8d3] bg-[#eaf8f1] p-3">
                          <div className="text-sm font-semibold text-[#1f8f5b] mb-2">Strengths</div>
                          <div className="space-y-2">
                            {!domainStrengths.length ? (
                              <div className="text-xs text-[#617587]">No strengths in this domain.</div>
                            ) : domainStrengths.map((item, i) => (
                              <div key={`sq-st-${domainIdx}-${i}`} className="bg-white border border-[#cfe8dc] rounded-[10px] p-2.5">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="text-sm text-[#233445]">{item.title}</div>
                                  <Tag color="green">L{item.level}</Tag>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-[12px] border border-[#f5d89a] bg-[#fff7e7] p-3">
                          <div className="text-sm font-semibold text-[#b8611c] mb-2">Areas for Improvement</div>
                          <div className="space-y-2">
                            {!domainImprovements.length ? (
                              <div className="text-xs text-[#617587]">No improvement areas in this domain.</div>
                            ) : domainImprovements.map((item, i) => (
                              <div key={`sq-imp-${domainIdx}-${i}`} className="bg-white border border-[#f7c9a1] rounded-[10px] p-2.5">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="text-sm text-[#233445]">{item.title}</div>
                                  <Tag color={item.level <= 1 ? "red" : "orange"}>L{item.level}</Tag>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Category</TH><TH>Finding</TH><TH>Severity</TH><TH>Urgency</TH></tr></thead>
                  <tbody>
                    {!findings.length ? <EmptyRow cols={4} msg="No diagnosis findings available yet." /> :
                      findings.map((f,i) => (
                        <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                          <TD>{f.category}</TD><TDstrong>{f.title}</TDstrong>
                          <TD><Tag color={sevColor(f.severity)}>{f.severity}</Tag></TD>
                          <TD><Tag color={urgColor(f.urgency)}>{f.urgency}</Tag></TD>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </Window>

          {/* SWOT */}
          <Window id="swotSection" title="SWOT View" note="Strengths, weaknesses, opportunities, and threats in one place.">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {swotQuadrants.map(q => {
                const pts = (swot.points||[]).filter(p => p.quadrant === q.key);
                return (
                  <div key={q.key} className={`rounded-[14px] border ${q.border} overflow-hidden`}>
                    <div className={`${q.bg} px-4 py-2 font-semibold text-sm ${q.textColor}`}>{q.label}</div>
                    <div className="p-3 space-y-2">
                      {!pts.length ? <div className="text-xs text-[#8ea0b1]">No {q.label.toLowerCase()} yet.</div> :
                        pts.map((p,i) => (
                          <div key={i} className="bg-white rounded-[10px] p-2.5 border border-[#e6ecf3]">
                            <div className="text-sm font-medium text-[#233445]">{p.title}</div>
                            {viewMode === "detailed" && p.description && <div className="text-xs text-[#617587] mt-0.5">{p.description}</div>}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </Window>

          {/* GOALS */}
          <Window id="goalsSection" title="Strategic Goals" note="Goal categories, owners, timelines, KPIs, and status.">
            <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Category</TH><TH>Goal</TH><TH>Owner</TH><TH>Timeline</TH><TH>KPI</TH></tr></thead>
                <tbody>
                  {!goals.length ? <EmptyRow cols={5} msg="No goal data available yet." /> :
                    goals.map((g,i) => (
                      <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                        <TD>{g.category}</TD><TDstrong>{g.title}</TDstrong>
                        <TD>{g.owner||"—"}</TD><TD>{g.timeline||"—"}</TD><TD>{g.kpi||"—"}</TD>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </Window>

          {/* ACTIONS */}
          <Window id="actionsSection" title="Action Plans" note="Task-level execution mapped against goals.">
            <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Linked Goal</TH><TH>Action Item</TH><TH>Owner</TH><TH>Status</TH><TH>Timeline</TH></tr></thead>
                <tbody>
                  {!actions.length ? <EmptyRow cols={5} msg="No action plan data available yet." /> :
                    actions.map((a,i) => (
                      <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                        <TD>{a.goal||"—"}</TD><TDstrong>{a.title}</TDstrong>
                        <TD>{a.owner||"—"}</TD>
                        <TD><Tag color={a.status==="Completed"?"green":a.status==="In Progress"?"blue":a.status==="Blocked"?"red":"orange"}>{a.status||"—"}</Tag></TD>
                        <TD>{[a.startDate, a.endDate].filter(Boolean).join(" → ")||"—"}</TD>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </Window>

          {/* MONITORING */}
          <Window id="monitoringSection" title="Monitoring & Review Logs" note="Progress review entries, blockers, and next checkpoints.">
            <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]"><TH>Linked Goal</TH><TH>Linked Action</TH><TH>Status</TH><TH>Review Date</TH><TH>Blocker / Risk</TH></tr></thead>
                <tbody>
                  {!reviews.length ? <EmptyRow cols={5} msg="No monitoring data available yet." /> :
                    reviews.map((r,i) => (
                      <tr key={i} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe]">
                        <TD>{r.goal||"—"}</TD><TD>{r.action||"—"}</TD>
                        <TD><Tag color={r.status==="On Track"?"green":r.status==="At Risk"?"orange":r.status==="Blocked"?"red":"blue"}>{r.status||"—"}</Tag></TD>
                        <TD>{r.reviewDate||"—"}</TD>
                        <TD className="max-w-[200px]">{r.blocker||"None"}</TD>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </Window>

          {/* FINAL SUMMARY */}
          <Window id="reportSection" title="Final Summary & Reporting View" note="A final connected interpretation of the whole school portal.">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              <PCard label="Overall Strategic Commentary" value={commentary} />
              <PCard label="Major Highlights" value={highlights.join(" | ")} />
              <PCard label="Concern Areas" value={concerns.join(" | ")} />
              <PCard label="Recommended Next Step" value={nextStep} />
            </div>
            <div className="bg-[#eef6fc] rounded-[14px] border border-[#cfe3f3] p-4">
              <div className="text-sm font-semibold text-[#2d81c1] mb-1">Print note</div>
              <div className="text-sm text-[#617587]">Use the print button to export this compiled page for meetings, presentations, or internal working review.</div>
            </div>
          </Window>

          <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8 print:hidden">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1D3469]">View Actions</h3>
                <p className="text-sm text-gray-500">Save, refresh, and export the compiled school view.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={handleSaveSnapshot} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                  Save Snapshot
                </button>
                <button onClick={handleRefresh} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                  Refresh View
                </button>
                <button onClick={handlePrint} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                  Print View
                </button>
              </div>
            </div>
          </section>
          </main>
        </div>
      </div>
      <Toast />
    </>
  );
}
